import React, { useContext, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { Bucket, GetSalesInsightsAggregatedRequest, SalesInsightsAggregate, SalesInsightsAggregateItem, useSalesInsightsAggregated } from '../../../../api/indicators';
import { dateRange, formatDateParam } from '../../../../helpers/dateUtils';
import { DashboardFiltersContext } from '../filters/DashboardFiltersContext';
import { DashboardContext } from '../../DashboardContext';
import AreaChart from '../charts/areaChart/AreaChart';
import { AreaChartDataItem } from '../charts/areaChart/AreaChartBase';
import { SettingsContext } from '../../../../context/SettingsContext';
import { getBucketFromRange } from '../../dashboardHelpers';

const NetMargin = () => {
    const { t } = useTranslation();
    const { calendarPreferences } = useContext(SettingsContext);
    const { filters, dateRangeConfig } = useContext(DashboardFiltersContext);
    const { companyConfig, refreshDate } = useContext(DashboardContext);
    const [lineBarChartData, setLineBarChartData] = useState<AreaChartDataItem[]>([]);

    const initialStartDate = useMemo(() => filters.periodFrom
        ? filters.periodFrom
        : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate, [filters.periodFrom]);

    const initialEndDate = useMemo(() => filters.periodTo
        ? filters.periodTo
        : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate, [filters.periodTo]);


    const [params, setParams] = useState<GetSalesInsightsAggregatedRequest>({
        fields: 'netMargin',
        startDate: formatDateParam(initialStartDate),
        endDate: formatDateParam(initialEndDate),
        locations: filters?.locations?.join(',') ? filters?.locations?.join(',') : companyConfig?.locations?.map(location => location.id).join(','),
        locationArea: filters?.locationArea?.id,
        businessStartHour: calendarPreferences.daysStart,
        bucket: getBucketFromRange(initialStartDate, initialEndDate)
    });

    const {
        data: salesInsightsAggregate,
        isLoading,
        isError,
        refetch: fetchSalesAndTransactions
    } = useSalesInsightsAggregated<SalesInsightsAggregate>(params, 'sales-insights-aggregate-net-margin');

    useEffect(() => {
        if (salesInsightsAggregate) {
            setLineBarChartData(
                salesInsightsAggregate.salesInsightsAggregate?.map(
                    (salesInsightsAggregateItem) => ({
                        dateTime: new Date(salesInsightsAggregateItem.dateTime),
                        "Net Margin": salesInsightsAggregateItem.salesInsights.netMargin ?? 0
                    })
                )
            );
        }
    }, [salesInsightsAggregate]);

    useEffect(() => {

        const start = filters.periodFrom
            ? filters.periodFrom
            : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate;

        const end = filters.periodTo
            ? filters.periodTo
            : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate;

        setParams({
            ...params,
            startDate: formatDateParam(start),
            endDate: formatDateParam(end),
            locations: filters?.locations?.join(','),
            locationArea: filters?.locationArea?.id,
            bucket: getBucketFromRange(start, end)
        });
    }, [filters]);

    useEffect(() => { fetchSalesAndTransactions(); }, [params]);

    useEffect(() => {
        if (refreshDate != null) {
            fetchSalesAndTransactions();
        }
    }, [refreshDate]);

    return (
        <AreaChart
            title={t('screens.dashboarding.charts.netMargin')}
            currency={salesInsightsAggregate?.currency}
            description={t('screens.dashboarding.charts.excludingTax')}
            isError={isError}
            isLoading={isLoading}
            leftAxisLabel={t("screens.dashboarding.charts.amount")}
            lineLabels={[
                t("screens.dashboarding.charts.netMargin"),
            ]}
            data={lineBarChartData}
            bucket={params.bucket as Bucket}
            setBucket={
                (value: string) => setParams({
                    ...params,
                    bucket: value as Bucket
                })
            }
            height={460}
        />
    )
}

export default NetMargin;

